import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ContactBulkResult,
  ContactInput,
  ContactListResponse,
  ContactPatch,
  ContactResponse,
} from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

const CONTACTS_KEY = "contacts" as const;

/** A tri-state presence filter's active values; `undefined` = no filter. */
export type PresenceFilter = "yes" | "no";

/**
 * The contacts-list filter selection held by the UI. Each presence filter is
 * tri-state ("any" | "yes" | "no"); `tag` is a single tag name ("" = none).
 */
export interface ContactsFilters {
  hasEmail: "any" | PresenceFilter;
  hasPhone: "any" | PresenceFilter;
  hasAddress: "any" | PresenceFilter;
  tag: string;
}

export const EMPTY_CONTACTS_FILTERS: ContactsFilters = {
  hasEmail: "any",
  hasPhone: "any",
  hasAddress: "any",
  tag: "",
};

/** True when any filter is set away from its "no filter" default. */
export function hasActiveFilters(f: ContactsFilters): boolean {
  return (
    f.hasEmail !== "any" ||
    f.hasPhone !== "any" ||
    f.hasAddress !== "any" ||
    Boolean(f.tag)
  );
}

export interface ContactsListParams {
  search?: string;
  householdId?: string;
  listId?: string;
  excludeListId?: string;
  hasEmail?: PresenceFilter;
  hasPhone?: PresenceFilter;
  hasAddress?: PresenceFilter;
  tag?: string;
  /** 1-based page index for offset pagination. */
  page?: number;
  limit?: number;
}

function buildContactsQuery(params: ContactsListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set("search", params.search);
  if (params.householdId) search.set("householdId", params.householdId);
  if (params.listId) search.set("listId", params.listId);
  if (params.excludeListId) search.set("excludeListId", params.excludeListId);
  // Presence filters serialize to true/false; omitted means no filter.
  if (params.hasEmail) search.set("hasEmail", params.hasEmail === "yes" ? "true" : "false");
  if (params.hasPhone) search.set("hasPhone", params.hasPhone === "yes" ? "true" : "false");
  if (params.hasAddress) search.set("hasAddress", params.hasAddress === "yes" ? "true" : "false");
  if (params.tag) search.set("tag", params.tag);
  if (params.page && params.page > 1) search.set("page", String(params.page));
  if (params.limit) search.set("limit", String(params.limit));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function useContactsList(params: ContactsListParams = {}) {
  return useQuery<ContactListResponse, ApiClientError>({
    queryKey: [CONTACTS_KEY, "list", params],
    queryFn: () => apiFetch<ContactListResponse>(`/contacts${buildContactsQuery(params)}`),
    // Keep the prior page's rows mounted while the next page loads. This
    // preserves the list height (so the scroll-to-top has somewhere to scroll
    // from) and lets the new data cross-fade in instead of flashing a skeleton.
    placeholderData: keepPreviousData,
  });
}

export function useContact(id: string | undefined) {
  return useQuery<ContactResponse, ApiClientError>({
    queryKey: [CONTACTS_KEY, "detail", id],
    queryFn: () => apiFetch<ContactResponse>(`/contacts/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation<ContactResponse, ApiClientError, ContactInput>({
    mutationFn: (body) =>
      apiFetch<ContactResponse>("/contacts", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (contact) => {
      qc.setQueryData([CONTACTS_KEY, "detail", contact.id], contact);
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: ["households"] });
    },
  });
}

export function useUpdateContact(id: string) {
  const qc = useQueryClient();
  return useMutation<ContactResponse, ApiClientError, ContactPatch>({
    mutationFn: (body) =>
      apiFetch<ContactResponse>(`/contacts/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (contact) => {
      qc.setQueryData([CONTACTS_KEY, "detail", contact.id], contact);
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: ["households"] });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/contacts/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: [CONTACTS_KEY, "detail", id] });
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: ["households"] });
    },
  });
}

/**
 * Bulk-delete contacts via POST /contacts/bulk. Mirrors useDeleteContact's
 * cache cleanup across every deleted ID.
 */
export function useDeleteContacts() {
  const qc = useQueryClient();
  return useMutation<ContactBulkResult, ApiClientError, string[]>({
    mutationFn: (ids) =>
      apiFetch<ContactBulkResult>("/contacts/bulk", {
        method: "POST",
        body: JSON.stringify({ operation: "delete", ids }),
      }),
    onSuccess: (_res, ids) => {
      for (const id of ids) {
        qc.removeQueries({ queryKey: [CONTACTS_KEY, "detail", id] });
      }
      qc.invalidateQueries({ queryKey: [CONTACTS_KEY, "list"] });
      qc.invalidateQueries({ queryKey: ["households"] });
    },
  });
}
